import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDatabase } from '../context/DatabaseContext';
import { 
  getAlertsForInstitution, 
  getUnreadAlertCount,
  getAlertStatistics,
  markAlertAsRead,
  markAlertAsResolved,
  dismissAlert 
} from '../services/alertService';

const SPOCDashboard = () => {
  const navigate = useNavigate();
  const { certificates } = useDatabase();
  const [stats, setStats] = useState({
    totalCertificates: 0,
    pendingAlerts: 0,
    recentUploads: 0,
    verificationRate: 0
  });
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleLogout = () => {
    console.log('🚪 Logging out');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    navigate('/login');
  };

  // Load alerts
  useEffect(() => {
    const loadAlerts = async () => {
      if (!user.institutionId) {
        setLoadingAlerts(false);
        return;
      }

      try {
        // Get unread alerts
        const alertData = await getAlertsForInstitution(user.institutionId, {
          status: 'unread',
          limit: 10
        });
        setAlerts(alertData);

        // Get alert count
        const count = await getUnreadAlertCount(user.institutionId);
        setStats(prev => ({ ...prev, pendingAlerts: count }));
      } catch (error) {
        console.error('Failed to load alerts:', error);
      } finally {
        setLoadingAlerts(false);
      }
    };

    loadAlerts();
    // Refresh alerts every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, [user.institutionId]);

  useEffect(() => {
    // Check authentication
    if (!localStorage.getItem('isAuthenticated') || user.role !== 'spoc') {
      navigate('/login');
      return;
    }

    const certArray = certificates || [];

    // Calculate stats
    const total = certArray.length;
    const recent = certArray.filter(cert => {
      const uploadDate = new Date(cert.uploadDate || cert.upload_date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return uploadDate >= weekAgo;
    }).length;

    setStats(prev => ({
      ...prev,
      totalCertificates: total,
      recentUploads: recent,
      verificationRate: total > 0 ? Math.round((total - 2) / total * 100) : 0
    }));
  }, [certificates, navigate, user.role]);

  const handleMarkAsRead = async (alertId) => {
    await markAlertAsRead(alertId);
    setAlerts(alerts.filter(a => a.id !== alertId));
    setStats(prev => ({ ...prev, pendingAlerts: Math.max(0, prev.pendingAlerts - 1) }));
  };

  const handleResolve = async (alertId) => {
    await markAlertAsResolved(alertId, user.id);
    setAlerts(alerts.filter(a => a.id !== alertId));
    setStats(prev => ({ ...prev, pendingAlerts: Math.max(0, prev.pendingAlerts - 1) }));
  };

  const handleDismiss = async (alertId) => {
    await dismissAlert(alertId);
    setAlerts(alerts.filter(a => a.id !== alertId));
    setStats(prev => ({ ...prev, pendingAlerts: Math.max(0, prev.pendingAlerts - 1) }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Wave Background */}
      <div className="page-wave-background">
        <img src="/hero-wave.svg" alt="" className="wave-image" />
      </div>
      {/* Professional Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">SPOC Dashboard</h1>
                <p className="text-sm text-gray-600 mt-1">{user.organization || 'Certificate Management Portal'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-right mr-4">
                <p className="text-sm font-semibold text-gray-900">{user.email}</p>
                <p className="text-xs text-gray-500">Single Point of Contact</p>
              </div>
              
              <Link 
                to="/upload" 
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:shadow-lg transition-all"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload
              </Link>
              
              <Link 
                to="/check" 
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Verify
              </Link>
              
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-100 p-2">
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'overview' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('alerts')}
              className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'alerts' 
                  ? 'bg-yellow-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Alerts
              {stats.pendingAlerts > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {stats.pendingAlerts}
                </span>
              )}
            </button>
            <button 
              onClick={() => setActiveTab('certificates')}
              className={`flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'certificates' 
                  ? 'bg-green-600 text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Certificates
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* System Analytics Header */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">System Analytics</h2>
              <p className="text-sm text-gray-600 mt-1">Overview of certificates and system performance</p>
            </div>

            {/* Stats Grid - 4 Cards in a Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Certificates */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Total Certificates</p>
                    <p className="text-4xl font-bold text-gray-900">{stats.totalCertificates}</p>
                    <p className="text-xs text-gray-500 mt-2">Issued by institution</p>
                  </div>
                  <div className="bg-blue-100 p-4 rounded-full">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Pending Alerts */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-2">Pending Alerts</p>
                    <p className="text-4xl font-bold text-gray-900">{stats.pendingAlerts}</p>
                    <p className="text-xs text-gray-500 mt-2">Require review</p>
                  </div>
                  <div className="bg-yellow-100 p-4 rounded-full">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.964-1.333-3.732 0L3.268 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Recent Uploads */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2">Recent (7d)</p>
                    <p className="text-4xl font-bold text-gray-900">{stats.recentUploads}</p>
                    <p className="text-xs text-gray-500 mt-2">Last week uploads</p>
                  </div>
                  <div className="bg-green-100 p-4 rounded-full">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Verification Rate */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2">Verification</p>
                    <p className="text-4xl font-bold text-gray-900">{stats.verificationRate}%</p>
                    <p className="text-xs text-gray-500 mt-2">Success rate</p>
                  </div>
                  <div className="bg-purple-100 p-4 rounded-full">
                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Grid */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
              <p className="text-sm text-gray-600 mb-6">Manage certificates and system operations</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/upload"
                  className="bg-white rounded-xl shadow-lg border-t-4 border-blue-500 p-6 hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-600 transition-all">
                      <svg className="w-6 h-6 text-blue-600 group-hover:text-white transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-3">Upload Certificates</h3>
                  </div>
                  <p className="text-sm text-gray-600">Issue and upload new certificates for students</p>
                </Link>

                <Link
                  to="/check"
                  className="bg-white rounded-xl shadow-lg border-t-4 border-green-500 p-6 hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-600 transition-all">
                      <svg className="w-6 h-6 text-green-600 group-hover:text-white transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-3">Verify Certificates</h3>
                  </div>
                  <p className="text-sm text-gray-600">Check certificate authenticity and validity</p>
                </Link>

                <Link
                  to="/admin"
                  className="bg-white rounded-xl shadow-lg border-t-4 border-purple-500 p-6 hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-purple-100 p-3 rounded-lg group-hover:bg-purple-600 transition-all">
                      <svg className="w-6 h-6 text-purple-600 group-hover:text-white transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-3">View Analytics</h3>
                  </div>
                  <p className="text-sm text-gray-600">Monitor certificate trends and analytics</p>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Student Alerts</h2>
                <p className="text-sm text-gray-600 mt-1">Certificate verification alerts requiring attention</p>
              </div>
              {stats.pendingAlerts > 0 && (
                <div className="bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-semibold">
                  {stats.pendingAlerts} Pending
                </div>
              )}
            </div>
            
            {loadingAlerts ? (
              <div className="flex items-center justify-center p-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-yellow-600"></div>
                <p className="ml-4 text-gray-600 font-medium">Loading alerts...</p>
              </div>
            ) : alerts.length === 0 ? (
              <div className="p-12 bg-green-50 border-2 border-green-200 rounded-xl text-center">
                <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg font-bold text-gray-900">All Clear!</p>
                <p className="text-sm text-gray-600 mt-2">No pending alerts at this time. System operating normally.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id} 
                    className={`p-6 rounded-xl border-l-4 shadow-md hover:shadow-lg transition-all ${
                      alert.severity === 'critical' 
                        ? 'bg-red-50 border-red-500' 
                        : alert.severity === 'warning'
                        ? 'bg-yellow-50 border-yellow-500'
                        : 'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1">
                        {alert.severity === 'critical' ? (
                          <div className="bg-red-500 p-2 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : alert.severity === 'warning' ? (
                          <div className="bg-yellow-500 p-2 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : (
                          <div className="bg-blue-500 p-2 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        <div className="ml-4 flex-1">
                          <h4 className="text-lg font-bold text-gray-900">{alert.title}</h4>
                          <p className="text-sm text-gray-700 mt-1 leading-relaxed">{alert.message}</p>
                          
                          <div className="mt-3 space-y-2">
                            {alert.student_name && (
                              <div className="flex items-center text-sm bg-white/70 rounded-lg px-3 py-2">
                                <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <span className="font-semibold">Student:</span>
                                <span className="ml-2">{alert.student_name}</span>
                                {alert.roll_number && <span className="ml-2 text-gray-500">({alert.roll_number})</span>}
                              </div>
                            )}
                            
                            {alert.verification_attempt_by && (
                              <div className="flex items-center text-xs text-gray-600 bg-white/50 rounded px-2 py-1">
                                <span className="font-medium">Verified by:</span>
                                <span className="ml-1">{alert.verification_attempt_by}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center text-xs text-gray-500">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {formatDate(alert.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-4 flex flex-col gap-2">
                        <button
                          onClick={() => handleResolve(alert.id)}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
                          title="Mark as resolved"
                        >
                          ✓ Resolve
                        </button>
                        <button
                          onClick={() => handleDismiss(alert.id)}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
                          title="Dismiss alert"
                        >
                          × Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Certificates Tab */}
        {activeTab === 'certificates' && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Recent Certificates</h2>
                <p className="text-sm text-gray-600 mt-1">Recently issued certificates from your institution</p>
              </div>
              <Link
                to="/upload"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-md"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload New
              </Link>
            </div>
            
            {((certificates || []).length === 0) ? (
              <div className="p-12 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-semibold text-gray-900">No certificates uploaded yet</p>
                <p className="text-sm text-gray-500 mt-2">Use the Upload Certificate button to issue your first certificate</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(certificates || []).map((cert, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all border border-gray-200">
                    <div className="flex items-center flex-1">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-semibold text-gray-900">{cert.fileName || cert.file_name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Uploaded:</span> {cert.uploadDate || cert.upload_date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SPOCDashboard;
